import { useEffect, useState } from 'react';
import Layout from '../components/Layout';

const Download = () => {
  const [userOS, setUserOS] = useState<'Windows' | 'macOS' | 'Linux' | 'iOS' | 'Unknown'>('Unknown');
  const [currentIndex, setCurrentIndex] = useState(0);

  const downloadOptions = [
    {
      id: 'windows',
      icon: '🪟',
      name: 'Windows',
      description: 'For Windows 7 and above',
      recommended: userOS === 'Windows',
      buttons: [
        { label: 'Download', href: '#' }
      ]
    },
    {
      id: 'macos',
      icon: '🍎',
      name: 'macOS',
      description: 'For Mac OS X 10.14 and later',
      recommended: userOS === 'macOS',
      comingSoon: true,
      badge: 'In Development'
    },
    {
      id: 'linux',
      icon: '🐧',
      name: 'Linux',
      description: 'For Ubuntu 18.04 and later',
      recommended: userOS === 'Linux',
      comingSoon: true,
      badge: 'In Development'
    },
    {
      id: 'ios',
      icon: '📱',
      name: 'iOS',
      description: 'For iPhone and iPad',
      comingSoon: true,
      badge: 'Coming Soon'
    },
    {
      id: 'android',
      icon: '🤖',
      name: 'Android',
      description: 'Coming soon to Google Play Store',
      comingSoon: true,
      badge: 'In Development'
    }
  ];
  // Popup state for Windows download
  const [showWinPopup, setShowWinPopup] = useState(false);

  // Download links for Windows
  const windowsLinks = [
    {
      label: 'EXE Installer',
      url: 'https://github.com/azinoreva/PascalineKids/releases/download/PascalineKids/PascalineKids.Setup.1.0.0.exe',
    },
    {
      label: 'MSI Installer',
      url: 'https://github.com/azinoreva/PascalineKids/releases/download/PascalineKids/PascalineKids.1.0.0.msi',
    },
    {
      label: 'ZIP (Portable)',
      url: 'https://github.com/azinoreva/PascalineKids/releases/download/PascalineKids/pascaline.unpacked.zip',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % downloadOptions.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [downloadOptions.length]);

  const getVisibleItems = () => {
    const items = [];
    for (let i = 0; i < 3; i++) {
      items.push(downloadOptions[(currentIndex + i) % downloadOptions.length]);
    }
    return items;
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % downloadOptions.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + downloadOptions.length) % downloadOptions.length);
  };

  useEffect(() => {
    // Detect user's operating system
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.indexOf('win') > -1) {
      setUserOS('Windows');
    } else if (userAgent.indexOf('mac') > -1) {
      setUserOS('macOS');
    } else if (userAgent.indexOf('linux') > -1) {
      setUserOS('Linux');
    } else if (userAgent.indexOf('iphone') > -1 || userAgent.indexOf('ipad') > -1) {
      setUserOS('iOS');
    }
  }, []);

  return (
    <Layout>
      <div className="download-page">
        <section className="download-hero">
          <h1>Download Pascaline Kids</h1>
          <p>Get Pascaline Kids on your device and start learning today</p>
        </section>

      <section className="download-area">
        <h2>Choose Your Platform</h2>
        
        <div className="carousel-container">
          <button className="carousel-button prev" onClick={prevSlide} aria-label="Previous">
            ❮
          </button>
          
          <div className="carousel-track">
            {getVisibleItems().map((option) => (
              <div key={option.id} className="carousel-item">
                {option.comingSoon ? (
                  <div className="download-card coming-soon">
                    <div className="os-icon">{option.icon}</div>
                    <h3>{option.name}</h3>
                    <p>{option.description}</p>
                    <span className="dev-badge">{option.badge}</span>
                    <button className="download-button disabled" disabled>Coming Soon</button>
                  </div>
                ) : (
                  <div className={`download-card ${option.recommended ? 'recommended' : ''}`}>
                    <div className="os-icon">{option.icon}</div>
                    <h3>{option.name}</h3>
                    <p>{option.description}</p>
                    {option.recommended && <span className="recommended-badge">Recommended for you</span>}
                    <div className="download-buttons">
                      {option.id === 'windows' ? (
                        <button
                          className="download-button"
                          onClick={() => setShowWinPopup(true)}
                        >
                          Download
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Windows Download Popup (rendered at root of download-area) */}
          {showWinPopup && (
            <div className="popup-overlay">
              <div className="popup-content">
                <h3>Download for Windows</h3>
                <p>Select your preferred installer:</p>
                <ul className="win-download-list">
                  {windowsLinks.map((link) => (
                    <li key={link.label}>
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="download-link">{link.label}</a>
                    </li>
                  ))}
                </ul>
                <button className="close-popup" onClick={() => setShowWinPopup(false)}>Close</button>
              </div>
            </div>
          )}
          
          <button className="carousel-button next" onClick={nextSlide} aria-label="Next">
            ❯
          </button>
        </div>

        <div className="carousel-dots">
          {downloadOptions.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      <section className="download-info">
        <h2>Installation Help</h2>
        <div className="info-grid">
          <div className="info-card">
            <h4>System Requirements</h4>
            <p>
              <b>Minimum RAM:</b> 4 GB (with only TinyLlama model, and you must close all other apps for best results).<br />
              <b>Recommended RAM:</b> 8 GB or more for smoother experience and to use larger AI models.<br />
              Ensure your device meets these requirements for smooth performance.
            </p>
          </div>
          <div className="info-card">
            <h4>Installation Guide</h4>
            <p>
              <strong>Note:</strong> Pascaline Kids currently requires <b>Ollamma</b> to be installed for offline AI features.<br />
              During setup, Ollamma will be installed automatically if not already present.<br /><br />
              <b>If you do not want to install anything</b>, you can use the ZIP version. Simply extract the ZIP and start the app from the extracted folder. For convenience, it is recommended to create a shortcut for the app on your desktop.
            </p>
          </div>
        </div>
      </section>
      </div>
    </Layout>
  );
};

export default Download;
